import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import Select, { MultiValue } from 'react-select';
import { useGetDropdownItemsByCategory } from 'src/hooks/dropdownMenu.hooks';
import { useGetEligibleDrives } from 'src/hooks/offlineDrive.hooks';
import { useUserContext } from 'src/context/UserProvider';

interface DriveLabelFilterInputProps {
    showSelect?: boolean;
}

const DriveLabelFilterInput: React.FC<DriveLabelFilterInputProps> = ({ showSelect = false }) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { selectedDrive } = useUserContext();
    const driveId = searchParams.get('driveId') || selectedDrive?.value;

    // Fetch eligible drives to get drive details
    const { data: eligibleDrivesData } = useGetEligibleDrives({ driveId: driveId || undefined });

    // Fetch placement drive labels from dropdown menu
    const { data: placementDriveData } = useGetDropdownItemsByCategory('PLACEMENT_DRIVE', undefined, true);

    // Get the selected drive details
    const selectedDriveDetails = useMemo(() => {
        if (!driveId) return null;
        if (eligibleDrivesData?.eligibleDrives) {
            return eligibleDrivesData.eligibleDrives.find(drive => drive._id === driveId);
        }
        return null;
    }, [driveId, eligibleDrivesData]);

    // Get drive label options based on selected drive's driveLabel field
    const driveLabelOptions = useMemo(() => {
        if (!selectedDriveDetails?.driveLabel) return [];

        const driveLabels: string[] = Array.isArray(selectedDriveDetails.driveLabel)
            ? selectedDriveDetails.driveLabel
            : [String(selectedDriveDetails.driveLabel)];

        if (driveLabels.length === 0) return [];

        // If placement drive data is available, try to match with dropdown menu items
        if (placementDriveData?.lmsTechStacks && placementDriveData.lmsTechStacks.length > 0) {
            const placementDriveMap = new Map<string, string>();
            placementDriveData.lmsTechStacks
                .filter(item => item.isActive)
                .forEach(item => {
                    const itemLabel = item.label || item.name;
                    placementDriveMap.set(itemLabel, itemLabel);
                    if (item.name && item.name !== itemLabel) {
                        placementDriveMap.set(item.name, itemLabel);
                    }
                });

            return driveLabels
                .map(driveLabel => {
                    const matchedLabel = placementDriveMap.get(driveLabel) || driveLabel;
                    return {
                        label: matchedLabel,
                        value: matchedLabel,
                    };
                })
                .filter((option, index, self) => {
                    const firstIndex = self.findIndex(o => o.label === option.label);
                    return index === firstIndex;
                });
        }

        // Fallback: show labels directly
        return driveLabels.map(label => ({
            label: label,
            value: label,
        }));
    }, [selectedDriveDetails, placementDriveData]);

    const selectedValues = useMemo(() => {
        const driveLabelParam = searchParams.get('driveLabel');
        if (!driveLabelParam) return [];
        try {
            const parsed = JSON.parse(driveLabelParam);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    }, [searchParams]);

    const handleChange = (selectedOptions: MultiValue<{ label: string; value: string }>) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (selectedOptions && selectedOptions.length > 0) {
            const values = selectedOptions.map(opt => opt.value);
            newSearchParams.set('driveLabel', JSON.stringify(values));
        } else {
            newSearchParams.delete('driveLabel');
        }
        setSearchParams(newSearchParams);
    };

    const selectedOptions = useMemo(() => {
        return driveLabelOptions.filter(opt => selectedValues.includes(opt.value));
    }, [driveLabelOptions, selectedValues]);

    if (!driveId || driveLabelOptions.length === 0) {
        return null;
    }

    return (
        <div className="w-full">
            <Select
                menuPlacement={'auto'}
                classNamePrefix="my-select-plans"
                closeMenuOnSelect={false}
                isMulti
                placeholder="Drive Label"
                options={driveLabelOptions}
                value={selectedOptions}
                onChange={handleChange}
                isClearable
                isSearchable
            />
        </div>
    );
};

export default DriveLabelFilterInput;

